"use client";

import React, { useState, useRef, forwardRef, useImperativeHandle } from "react";
import classNames from "classnames";

interface VideoWrapperProps {
  src: string;
  className?: string;
  autoPlay?: boolean;
  muted?: boolean;
  loop?: boolean;
  controls?: boolean;
  poster?: string;
}

const PlayIcon: React.FC<{ className?: string }> = ({ className }) => (
  <svg
    viewBox="0 0 24 24"
    fill="currentColor"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
  >
    <path d="M8 5v14l11-7z" />
  </svg>
);

const VideoWrapper = forwardRef<HTMLVideoElement, VideoWrapperProps>(({
  src,
  className = "",
  autoPlay = false,
  muted = false,
  loop = false,
  controls = true,
  poster,
}, ref) => {
  const [isPlaying, setIsPlaying] = useState(autoPlay);
  const [showControls, setShowControls] = useState(controls);
  const videoRef = useRef<HTMLVideoElement>(null);

  // Expose the video element to parent components
  useImperativeHandle(ref, () => videoRef.current as HTMLVideoElement);

  const handlePlayClick = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
      setShowControls(true);
    }
  };

  const handleVideoClick = () => {
    if (!isPlaying) {
      handlePlayClick();
    }
  };

  const videoClassName = classNames(
    "w-full h-full object-cover",
    className
  );

  const playButtonClassName = classNames(
    "absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10 transition-all duration-300",
    {
      "opacity-0 pointer-events-none": isPlaying,
      "opacity-100": !isPlaying,
    }
  );

  return (
    <div className="relative w-full h-full group cursor-pointer" onClick={handleVideoClick}>
      <video
        ref={videoRef}
        className={videoClassName}
        src={src}
        loop={loop}
        muted={muted}
        autoPlay={autoPlay}
        controls={showControls && isPlaying}
        poster={poster}
        playsInline
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      
      {/* Play Button Overlay */}
      <div className={playButtonClassName}>
        <button
          onClick={(e) => {
            e.stopPropagation();
            handlePlayClick();
          }}
          className="bg-blue hover:bg-blue-dark text-white rounded-full p-4 md:p-6 shadow-lg transition-all duration-300 hover:scale-110 focus:outline-none focus:ring-4 focus:ring-blue/50"
          aria-label="Play video"
        >
          <PlayIcon className="w-8 h-8 md:w-12 md:h-12 ml-1" />
        </button>
      </div>

      {/* Darkening overlay when not playing */}
      {!isPlaying && (
        <div className="absolute inset-0 bg-black/20 transition-opacity duration-300 group-hover:bg-black/30" />
      )}
    </div>
  );
});

VideoWrapper.displayName = 'VideoWrapper';

export default VideoWrapper;
